// @flow


import ko from 'rgs-knockout';
import {EventEmitter} from 'event-emitter';
import {logout, retryWithAuth} from 'myrgs-auth-helpers';

import {render} from 'app/rgs/utils';
import type {rendererArguments} from 'app/rgs/utils';

import { usingCfgStrategy } from '../configuration';
import api from './api';
import { MenuModel, IMenuModel } from './model';
import {menuTypes} from './types';

import type {Observable} from 'rgs-knockout/types';
import type {TUrlValues, TElementView} from '../api';
import type { IMenuConfiguration } from 'app-my-navigation/types.flow';


export class MenuController extends EventEmitter {
	_model: IMenuModel;
	_view: rendererArguments;

	_queue = [];

	_urlDependencies: Observable<{[string]: *}>= ko.observable({});

	static create (...args: any[]): MenuController {
		return new MenuController(...args);
	}

	get hasModel (): boolean {
		return Boolean(this._model);
	}

	constructor () {
		super();

		this._model = this._createMenuModel();
	}

	_createMenuModel (): MenuModel {
		return new MenuModel({
			onGetUrlDependencies: this._urlDependencies,
			cfg: usingCfgStrategy.createDefaultMenu(),
			onItemsFetch: this.fetchInitiateState,
			onLogout: logout,
		});
	}

	init (): void {
		this._model.requestItems();
	}

	withCfg (configuration: IMenuConfiguration): MenuController {
		this.use(configuration);

		return this;
	}

	withView (view: rendererArguments): MenuController {
		this._view = view;

		return this;
	}

	sayAboutRerenderingWithKey (data: TElementView): void {
		this._queue.push(() => this._model.setState({
			type: menuTypes.RESPONSIBLE_FOR_RENDERING_ELEMENT_CHANGED,
			payload: data,
		}));
	}

	updateUrlDependencies (data: TUrlValues): void {
		let result = null;

		if (Array.isArray(data)) {
			result = data.reduce((prev, {key, value}) => ({
				...prev,
				[key]: {value},
			}), {});
		} else {
			const {key, value} = data;
			result = {[key]: value};
		}

		this._urlDependencies({
			...this._urlDependencies(),
			...result,
		});
	}

	render = (): void => {
		render({
			...this._view,
			model: this._model,
		});
	};

	use (cfg: IMenuConfiguration): void {
		this._model.setState({
			type: menuTypes.MENU_CONFIGURATION_CHANGED,
			payload: cfg,
		});
	}

	async fetchMenuItems (): Promise<any> {
		return await retryWithAuth(() => api.fetchMenuItems());
	}

	async fetchSilentMenuItems (): Promise<any> {
		return await api.fetchMenuItems();
	}

	fetchInitiateState = async (): Promise<void> => {
		const data = await this.fetchMenuItems();

		this.setState(data);
	};

	setState = (data: any) => {
		this.emit(menuTypes.MENU_ITEMS_FETCHED, data);
		//1) listener from app-my-dms-insurance/page/page-controller;
		//2) you should continue...

		this._model.setState({
			type: menuTypes.MENU_ITEMS_FETCHED,
			payload: data,
		});

		this._queue.forEach(callback => callback());
	};

	async getCurrentMenuKey (): Promise<string> {
		const {Items: items} = await this.fetchMenuItems();

		const location = this._model.cfg.getCurrentLocation();
		const selected = (items || []).find(item => item.Url === location);

		return selected && selected.Key;
	}


	setActiveItemByKey (key: string): void {

		if (!this.hasModel) {
			this._queue.push(() => this._setActiveMenuItem(key));
			return;
		}

		this._setActiveMenuItem(key);
	}

	_setActiveMenuItem = (key: string): void => {
		const cfg = this._model.cfg;
		cfg.setActiveMenuItemByKey(key);
	}
}

export default new MenuController();
